import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

// Helper to auto-process item images for shelf view
// Returns the processedImageUrl on success, null on failure
const autoProcessImage = async (itemType, itemId) => {
  // Only process for shelf-compatible types
  if (!['console', 'lego', 'music', 'books'].includes(itemType)) {
    return null;
  }

  try {
    const response = await axiosInstance.post('/image-processor/process-item', {
      itemType,
      itemId
    });
    console.log(`Auto-processed image for ${itemType} ${itemId}`);
    return response.data?.processedImageUrl || null;
  } catch (error) {
    console.error('Auto-process failed:', error);
    return null;
  }
};

/**
 * Custom hook to manage collection data (games, consoles, lego sets)
 * @param {string} collectionType - Type of collection ('games', 'consoles', 'lego')
 * @param {string} userId - User ID to fetch collection for
 * @returns {Object} Collection data and management functions
 */
const useCollection = (collectionType, userId) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Determine the API endpoint based on collection type
  const getEndpoint = useCallback(() => {
    switch (collectionType) {
      case 'games':
        return `/games/user/${userId}`;
      case 'consoles':
        return `/consoles/user/${userId}`;
      case 'lego':
        return `/lego/user/${userId}`;
      case 'movies':
        return `/movies/user/${userId}`;
      case 'movie-wishlist':
        return `/movie-wishlist/user/${userId}`;
      case 'music':
        return `/music/user/${userId}`;
      case 'music-wishlist':
        return `/music-wishlist/user/${userId}`;
      case 'books':
        return `/books/user/${userId}`;
      case 'book-wishlist':
        return `/book-wishlist/user/${userId}`;
      case 'board-games':
        return `/board-games/user/${userId}`;
      case 'board-game-wishlist':
        return `/board-game-wishlist/user/${userId}`;
      case 'game-wishlist':
        return `/game-wishlist/user/${userId}`;
      case 'console-wishlist':
        return `/console-wishlist/user/${userId}`;
      case 'lego-wishlist':
        return `/lego-wishlist/user/${userId}`;
      case 'trading-cards':
        return `/trading-cards/user/${userId}`;
      case 'trading-cards-wishlist':
        return `/trading-cards/user/${userId}?wishlist=true`;
      case 'manga':
        return `/manga/user/${userId}`;
      case 'manga-wishlist':
        return `/manga/user/${userId}?wishlist=true`;
      case 'comics':
        return `/comics/user/${userId}`;
      case 'comics-wishlist':
        return `/comics/user/${userId}?wishlist=true`;
      default:
        console.warn(`Unknown collection type: ${collectionType}, defaulting to games`);
        return `/games/user/${userId}`;
    }
  }, [collectionType, userId]);

  // Fetch collection items
  const fetchItems = useCallback(async () => {
    if (!userId) {
      console.log('No userId available');
      return;
    }

    console.log(`Starting to fetch ${collectionType} for userId:`, userId);
    setLoading(true);
    setError('');
    try {
      const endpoint = getEndpoint();
      console.log('Making API request to:', endpoint);
      const response = await axiosInstance.get(endpoint);
      console.log(`${collectionType} Collection API response:`, response);
      console.log(`${collectionType} Collection API response data:`, response.data);
      console.log(`${collectionType} Collection API response data type:`, typeof response.data);
      console.log(`${collectionType} Collection API response data length:`, Array.isArray(response.data) ? response.data.length : 'Not an array');
      
      // Set the items state with the response data
      setItems(response.data);
      setError('');
    } catch (error) {
      console.error(`Error fetching ${collectionType}:`, error);
      console.error('Error details:', error.response?.data);
      setError(error.response?.data?.error || `Failed to fetch ${collectionType}`);
    } finally {
      setLoading(false);
    }
  }, [userId, collectionType, getEndpoint]);

  // Add item to collection
  const addItem = async (item) => {
    let endpoint;
    
    // Determine endpoint based on collection type
    switch (collectionType) {
      case 'games':
        endpoint = '/games';
        break;
      case 'consoles':
        endpoint = '/consoles';
        break;
      case 'lego':
        endpoint = '/lego';
        break;
      case 'movies':
        endpoint = '/movies';
        break;
      case 'game-wishlist':
        endpoint = '/game-wishlist';
        break;
      case 'console-wishlist':
        endpoint = '/console-wishlist';
        break;
      case 'lego-wishlist':
        endpoint = '/lego-wishlist';
        break;
      case 'movie-wishlist':
        endpoint = '/movie-wishlist';
        break;
      case 'music':
        endpoint = '/music';
        break;
      case 'music-wishlist':
        endpoint = '/music-wishlist';
        break;
      case 'books':
        endpoint = '/books';
        break;
      case 'book-wishlist':
        endpoint = '/book-wishlist';
        break;
      case 'board-games':
        endpoint = '/board-games';
        break;
      case 'board-game-wishlist':
        endpoint = '/board-game-wishlist';
        break;
      case 'trading-cards':
      case 'trading-cards-wishlist':
        endpoint = '/trading-cards';
        break;
      case 'manga':
      case 'manga-wishlist':
        endpoint = '/manga';
        break;
      case 'comics':
      case 'comics-wishlist':
        endpoint = '/comics';
        break;
      default:
        endpoint = '/games';
    }

    console.log(`Adding ${collectionType} item to collection at URL: ${endpoint}`);
    console.log('Item data:', item);
    
    try {
      const response = await axiosInstance.post(endpoint, item);
      console.log(`Successfully added ${collectionType} item:`, response.data);
      
      // Update items state with the new item
      setItems(prevItems => [...prevItems, response.data]);
      
      // Auto-process image for shelf view, then update item in state with processed URL
      const itemType = collectionType === 'consoles' ? 'console' : collectionType;
      if (response.data._id) {
        const addedItemId = response.data._id;
        autoProcessImage(itemType, addedItemId).then(processedUrl => {
          if (processedUrl) {
            setItems(prev => prev.map(i =>
              i._id === addedItemId ? { ...i, processedImageUrl: processedUrl } : i
            ));
          }
        });
      }
      
      // For movies and music, fetch all items again to ensure we have the latest data
      // This helps with the collection vs. wishlist distinction
      if (collectionType === 'movies' || collectionType === 'music' || collectionType === 'movie-wishlist' || collectionType === 'music-wishlist') {
        console.log(`Refreshing ${collectionType} collection after adding new item`);
        await fetchItems();
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error adding ${collectionType} item:`, error);
      console.error('Error details:', error.response?.data);
      
      // Enhanced error logging for validation errors
      if (error.response?.data?.errors) {
        console.error('Validation errors:', JSON.stringify(error.response.data.errors, null, 2));
        
        // Log each validation error separately for better debugging
        error.response.data.errors.forEach((err, index) => {
          console.error(`Validation error ${index + 1}:`, err.msg || err.message || JSON.stringify(err));
          if (err.path) console.error(`Field with error:`, err.path);
          if (err.value) console.error(`Invalid value:`, err.value);
        });
      }
      
      setError(error.response?.data?.error || `Failed to add ${collectionType} item. Please try again.`);
      throw error;
    }
  };

  // Update item in collection
  const updateItem = async (itemId, updatedData) => {
    try {
      let endpoint;
      switch (collectionType) {
        case 'games':
          endpoint = `/games/${itemId}`;
          break;
        case 'consoles':
          endpoint = `/consoles/${itemId}`;
          break;
        case 'lego':
          endpoint = `/lego/${itemId}`;
          break;
        case 'movies':
          endpoint = `/movies/${itemId}`;
          break;
        case 'movie-wishlist':
          endpoint = `/movie-wishlist/${itemId}`;
          break;
        case 'music':
          endpoint = `/music/${itemId}`;
          break;
        case 'music-wishlist':
          endpoint = `/music-wishlist/${itemId}`;
          break;
        case 'books':
          endpoint = `/books/${itemId}`;
          break;
        case 'book-wishlist':
          endpoint = `/book-wishlist/${itemId}`;
          break;
        case 'board-games':
          endpoint = `/board-games/${itemId}`;
          break;
        case 'board-game-wishlist':
          endpoint = `/board-game-wishlist/${itemId}`;
          break;
        case 'trading-cards':
        case 'trading-cards-wishlist':
          endpoint = `/trading-cards/${itemId}`;
          break;
        case 'manga':
        case 'manga-wishlist':
          endpoint = `/manga/${itemId}`;
          break;
        case 'comics':
        case 'comics-wishlist':
          endpoint = `/comics/${itemId}`;
          break;
        default:
          endpoint = `/games/${itemId}`;
      }

      const response = await axiosInstance.put(endpoint, updatedData);
      
      // Auto-process if image was updated
      const itemType = collectionType === 'consoles' ? 'console' : collectionType;
      if (updatedData.imageUrl || updatedData.platformLogo) {
        autoProcessImage(itemType, itemId).catch(err => {
          console.log('Background image processing queued');
        });
      }
      
      // Update the items list with the updated item
      setItems(prevItems =>
        prevItems.map(item =>
          item._id === itemId ? response.data : item
        )
      );

      setError('');
      return response.data;
    } catch (error) {
      console.error(`Error updating ${collectionType} item:`, error);
      setError(`Failed to update ${collectionType} item`);
      throw error;
    }
  };

  // Delete item from collection
  const deleteItem = async (itemId) => {
    console.log(`useCollection.deleteItem - Starting to delete item with ID: ${itemId}`);
    console.log(`useCollection.deleteItem - Collection type: ${collectionType}`);
    
    try {
      let endpoint;
      switch (collectionType) {
        case 'games':
          endpoint = `/games/${itemId}`;
          break;
        case 'consoles':
          endpoint = `/consoles/${itemId}`;
          break;
        case 'lego':
          endpoint = `/lego/${itemId}`;
          break;
        case 'movies':
          endpoint = `/movies/${itemId}`;
          break;
        case 'movie-wishlist':
          endpoint = `/movie-wishlist/${itemId}`;
          break;
        case 'music':
          endpoint = `/music/${itemId}`;
          break;
        case 'music-wishlist':
          endpoint = `/music-wishlist/${itemId}`;
          break;
        case 'game-wishlist':
          endpoint = `/game-wishlist/${itemId}`;
          break;
        case 'console-wishlist':
          endpoint = `/console-wishlist/${itemId}`;
          break;
        case 'lego-wishlist':
          endpoint = `/lego-wishlist/${itemId}`;
          break;
        case 'books':
          endpoint = `/books/${itemId}`;
          break;
        case 'book-wishlist':
          endpoint = `/book-wishlist/${itemId}`;
          break;
        case 'board-games':
          endpoint = `/board-games/${itemId}`;
          break;
        case 'board-game-wishlist':
          endpoint = `/board-game-wishlist/${itemId}`;
          break;
        case 'trading-cards':
        case 'trading-cards-wishlist':
          endpoint = `/trading-cards/${itemId}`;
          break;
        case 'manga':
        case 'manga-wishlist':
          endpoint = `/manga/${itemId}`;
          break;
        case 'comics':
        case 'comics-wishlist':
          endpoint = `/comics/${itemId}`;
          break;
        default:
          endpoint = `/games/${itemId}`;
      }

      console.log(`useCollection.deleteItem - Using endpoint: ${endpoint}`);
      const response = await axiosInstance.delete(endpoint);
      console.log(`useCollection.deleteItem - Response:`, response);
      
      if (response.status === 200) {
        console.log(`useCollection.deleteItem - Successfully deleted item with ID: ${itemId}`);
        setItems(prevItems => prevItems.filter(item => item._id !== itemId));
        setError('');
        return true;
      }
      console.log(`useCollection.deleteItem - Unexpected response status: ${response.status}`);
      return false;
    } catch (error) {
      console.error(`Error deleting ${collectionType} item:`, error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        setError('Authentication error. Please try logging in again.');
      } else if (error.response?.status === 403) {
        setError(`You do not have permission to delete this ${collectionType} item.`);
      } else if (error.response?.status === 404) {
        setError(`${collectionType} item not found. It may have been already deleted.`);
        setItems(prevItems => prevItems.filter(item => item._id !== itemId));
      } else {
        setError(error.response?.data?.error || `Failed to delete ${collectionType} item. Please try again.`);
      }
      throw error;
    }
  };

  // Filter items based on search query and platform
  const getFilteredItems = useCallback(() => {
    return items.filter(item => {
      // Search by name and other relevant properties
      let matchesSearch = true;
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        
        if (collectionType === 'lego') {
          // For Lego items, search across multiple properties
          matchesSearch = 
            (item.name && item.name.toLowerCase().includes(query)) ||
            (item.setNum && item.setNum.toLowerCase().includes(query)) ||
            (item.themeName && item.themeName.toLowerCase().includes(query)) ||
            (item.year && item.year.toString().includes(query));
        } else if (collectionType === 'movies') {
          // For movies, search across title, format, director, genre, year
          matchesSearch = 
            (item.name && item.name.toLowerCase().includes(query)) ||
            (item.format && item.format.toLowerCase().includes(query)) ||
            (item.director && item.director.toLowerCase().includes(query)) ||
            (item.genre && item.genre.toLowerCase().includes(query)) ||
            (item.year && item.year.toString().includes(query));
        } else if (collectionType === 'music' || collectionType === 'music-wishlist') {
          // For music, search across title, artist, format, genre, year
          matchesSearch =
            (item.name && item.name.toLowerCase().includes(query)) ||
            (item.artist && item.artist.toLowerCase().includes(query)) ||
            (item.format && item.format.toLowerCase().includes(query)) ||
            (item.genre && item.genre.toLowerCase().includes(query)) ||
            (item.year && item.year.toString().includes(query));
        } else if (collectionType === 'books' || collectionType === 'book-wishlist') {
          matchesSearch =
            (item.name && item.name.toLowerCase().includes(query)) ||
            (item.author && item.author.toLowerCase().includes(query)) ||
            (item.genre && item.genre.toLowerCase().includes(query)) ||
            (item.series && item.series.toLowerCase().includes(query)) ||
            (item.isbn13 && item.isbn13.includes(query)) ||
            (item.year && item.year.toString().includes(query));
        } else if (collectionType === 'board-games' || collectionType === 'board-game-wishlist') {
          matchesSearch =
            (item.name && item.name.toLowerCase().includes(query)) ||
            (item.designers && item.designers.toLowerCase().includes(query)) ||
            (item.publisher && item.publisher.toLowerCase().includes(query)) ||
            (item.category && item.category.toLowerCase().includes(query)) ||
            (item.year && item.year.toString().includes(query));
        } else {
          // For games and consoles
          matchesSearch = 
            (item.name && item.name.toLowerCase().includes(query)) ||
            (item.platform && item.platform.toLowerCase().includes(query));
        }
      }

      // Handle platform/theme/format filtering
      let matchesFilter = true;
      if (filterPlatform) {
        if (collectionType === 'lego') {
          // For Lego, filter by theme
          matchesFilter = 
            (item.themeName && item.themeName === filterPlatform) || 
            (item.themeId && item.themeId.toString() === filterPlatform);
        } else if (collectionType === 'movies') {
          // For movies, filter by format
          // Handle both string formats and object formats with value property
          console.log(`Filtering movie with format: ${JSON.stringify(item.format)}, against filter: ${filterPlatform}`);
          
          // If no filter is selected, show all items
          if (!filterPlatform) {
            matchesFilter = true;
          } else if (typeof item.format === 'object' && item.format !== null) {
            // Handle object format with value property
            if (item.format.value) {
              matchesFilter = item.format.value === filterPlatform;
            } else if (item.format.label) {
              matchesFilter = item.format.label === filterPlatform;
            } else {
              matchesFilter = false;
            }
          } else {
            // Handle string format
            matchesFilter = item.format === filterPlatform;
          }
        } else if (collectionType === 'music') {
          // For music, filter by format
          // Handle both string formats and object formats with value property
          console.log(`Filtering music with format: ${JSON.stringify(item.format)}, against filter: ${filterPlatform}`);
          
          // If no filter is selected, show all items
          if (!filterPlatform) {
            matchesFilter = true;
          } else if (typeof item.format === 'object' && item.format !== null) {
            // Handle object format with value property
            if (item.format.value) {
              matchesFilter = item.format.value === filterPlatform;
            } else if (item.format.label) {
              matchesFilter = item.format.label === filterPlatform;
            } else {
              matchesFilter = false;
            }
          } else {
            // Handle string format
            matchesFilter = item.format === filterPlatform;
          }
        } else if (item.platform) {
          // For games/consoles, filter by platform
          // Special handling for PC platforms
          if (filterPlatform === 'PC (Microsoft Windows)') {
            matchesFilter = item.platform === 'PC (Microsoft Windows)' || item.platform === 'PC';
          } else {
            matchesFilter = item.platform === filterPlatform;
          }
        }
      }

      // Handle status filtering (played/watched/listened)
      let matchesStatus = true;
      if (filterStatus) {
        if (collectionType === 'games' || collectionType === 'game-wishlist') {
          if (filterStatus === 'completed') matchesStatus = item.completed === true;
          else if (filterStatus === 'in-progress') matchesStatus = item.played === true && item.completed !== true;
          else if (filterStatus === 'not-started') matchesStatus = item.played !== true;
        } else if (collectionType === 'movies' || collectionType === 'movie-wishlist') {
          if (filterStatus === 'completed') matchesStatus = item.watched === true;
          else if (filterStatus === 'not-started') matchesStatus = item.watched !== true;
        } else if (collectionType === 'music' || collectionType === 'music-wishlist') {
          if (filterStatus === 'completed') matchesStatus = item.listened === true;
          else if (filterStatus === 'not-started') matchesStatus = item.listened !== true;
        } else if (collectionType === 'books' || collectionType === 'book-wishlist') {
          if (filterStatus === 'completed') matchesStatus = item.read === true;
          else if (filterStatus === 'not-started') matchesStatus = item.read !== true;
        } else if (collectionType === 'board-games') {
          if (filterStatus === 'completed') matchesStatus = item.played === true;
          else if (filterStatus === 'not-started') matchesStatus = item.played !== true;
        } else if (collectionType === 'lego' || collectionType === 'lego-wishlist') {
          if (filterStatus === 'completed') matchesStatus = item.built === true;
          else if (filterStatus === 'not-started') matchesStatus = item.built !== true;
        }
      }

      // Books format filter
      if (filterPlatform && (collectionType === 'books' || collectionType === 'book-wishlist')) {
        matchesFilter = item.format === filterPlatform;
      }

      return matchesSearch && matchesFilter && matchesStatus;
    });
  }, [items, searchQuery, filterPlatform, filterStatus, collectionType]);

  // Initial fetch when component mounts or userId changes
  useEffect(() => {
    if (userId) {
      fetchItems();
    }
  }, [userId, fetchItems]);

  return {
    items,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filterPlatform,
    setFilterPlatform,
    filterStatus,
    setFilterStatus,
    fetchItems,
    addItem,
    updateItem,
    deleteItem,
    getFilteredItems,
    setError,
    setItems
  };
};

export default useCollection;
